'use client';

// next/compat/router: identical to next/router's useRouter, but returns null
// instead of throwing when rendered under the App Router.
import { useRouter } from 'next/compat/router';
import { useParams } from 'next/navigation';

/**
 * ### useProjectPublicId
 *
 * @description
 * Returns the project publicId from the current route's query params in a type-safe way.
 * Next.js query values can be string | string[] | undefined; this hook normalizes to string | undefined.
 *
 * @example
 * const publicId = useProjectPublicId();
 * if (publicId) {
 *   router.push(`/project/${publicId}/insights/explore`);
 * }
 *
 * @returns The project publicId string, or undefined if not present or not yet available
 */
export const useProjectPublicId = (): string | undefined => {
  const pagesQuery = useRouter()?.query;
  const appParams = useParams<{ publicId?: string | string[] }>();
  const publicId = pagesQuery ? pagesQuery.publicId : appParams?.publicId;
  return typeof publicId === 'string'
    ? publicId
    : Array.isArray(publicId)
      ? publicId[0]
      : undefined;
};
